// eslint-disable-next-line no-restricted-imports
import { format } from 'date-fns';
import { isNull, isUndefined } from 'lodash-es';

import { DATE_FORMATS } from '~/core/shared/constants';
import { Nullable } from '~/core/shared/types';
import {
  isTruthyStringOfBoolean,
  sanitizePrecision,
  stringToNumber,
} from '~/core/shared/utils/general';

export const convertToType =
  (type: 'boolean' | 'number') =>
  (value: string): any => {
    switch (type) {
      case 'boolean': {
        return isTruthyStringOfBoolean(value);
      }
      case 'number':
        return stringToNumber(value);
      default:
        throw new Error(`Unsupported type: ${type}`);
    }
  };

export const convertBooleanToStringLiteral = (
  value: boolean | null | undefined,
): 'true' | 'false' | undefined =>
  isNull(value) || isUndefined(value)
    ? undefined
    : (String(value) as 'true' | 'false');

/**
 * Convert the decimal representation of a percentage to a percentile
 * E.g. 0.011 = 1.1
 *
 * @param num
 */
export const convertDecimalToPercentile = (num: number) => {
  const rounded = parseFloat((num * 100).toFixed(7));

  if (Number.isInteger(rounded)) {
    return rounded.toString();
  }

  const decimalLength = `${rounded}`.split('.')[1].length;
  const precision = Math.max(2, decimalLength);

  return rounded.toFixed(precision);
};

/**
 * Convert a percentile to the decimal representation of a percentage
 * E.g. 1.1 = 0.011
 *
 * @param num
 */
export const convertPercentileToDecimal = (num: number): number => {
  const strNum = num.toString();

  // Check if there's a decimal point, if not, treat it as an integer
  const decimalIndex = strNum.indexOf('.');

  // Converts a whole number into a decimal format by inserting a decimal point two places from the end,
  // or for numbers less than 100, prepending with '0.' or '0.0' as needed.
  if (decimalIndex === -1) {
    if (strNum.length === 1) {
      return parseFloat(`0.0${strNum}`);
    }
    if (strNum.length === 2) {
      return parseFloat(`0.${strNum}`);
    }
    return parseFloat(`${strNum.slice(0, -2)}.${strNum.slice(-2)}`);
  }

  // Split the number into the part before and after the decimal point
  const beforeDecimal = strNum.slice(0, decimalIndex);
  const afterDecimal = strNum.slice(decimalIndex + 1);

  // Handle cases where the number is less than two digits before the decimal
  if (beforeDecimal.length < 2) {
    const newBeforeDecimal = '0';
    const newAfterDecimal = beforeDecimal.padStart(2, '0') + afterDecimal;
    return parseFloat(`${newBeforeDecimal}.${newAfterDecimal}`);
  }

  // Move the decimal two places to the left
  const newBeforeDecimal = beforeDecimal.slice(0, -2);
  const newAfterDecimal = beforeDecimal.slice(-2) + afterDecimal;

  return parseFloat(`${newBeforeDecimal || '0'}.${newAfterDecimal}`);
};

export const preserveEmpty =
  (callback: (value: any) => any) =>
  (value: any): any =>
    isNull(value) || isUndefined(value) || value === ''
      ? null
      : callback(value);

export const divideByOneHundred = (value: number): number =>
  sanitizePrecision(value / 100);

export const formatNumberAsString = (value: number): string =>
  value % 1 !== 0 ? value.toFixed(2) : value.toString();

export const timesOneHundred = (value: number): number =>
  sanitizePrecision(value * 100);

export const snakeCaseToSentenceCase = (value: string): string => {
  if (value.length === 0) {
    return '-';
  }

  const [a, ...b] = value.replace('_', ' ');

  return a.toUpperCase() + b.join('').toLowerCase();
};

export const currency = (value: number): string =>
  Number.isNaN(value)
    ? '-'
    : new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
      }).format(divideByOneHundred(value));

export const displayFallbackIfNaNOrZero =
  (fallbackValue = '-') =>
  (value: Nullable<string | number | undefined>): string => {
    if (value === null || value === undefined || value === '') {
      return fallbackValue;
    }

    const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ''));

    if (Number.isNaN(numeric) || numeric === 0) {
      return fallbackValue;
    }

    return String(value);
  };

export const percent = (value: number): string =>
  Number.isNaN(value)
    ? '-'
    : new Intl.NumberFormat('en-AU', {
        style: 'percent',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

export const toDateFormat =
  (dateFormat: string = DATE_FORMATS.pretty) =>
  (value?: Nullable<string>): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return format(date, dateFormat);
  };

export const addByPercentage =
  (percentage = 0) =>
  (value: any) => {
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) {
      throw new Error('Invalid input: value must be a number in string format');
    }
    return sanitizePrecision(
      numericValue * (1 + divideByOneHundred(percentage)),
    );
  };

export const reverseByPercentage =
  (percentage = 0) =>
  (value: any) => {
    const numericValue = parseFloat(value);
    if (Number.isNaN(numericValue)) {
      throw new Error('Invalid input: value must be a number in string format');
    }
    // return numericValue * (1 - percentage / 100);
    return sanitizePrecision(
      numericValue / (1 + divideByOneHundred(percentage)),
    );
  };
